import { Injectable, computed, inject } from '@angular/core';
import { SesionService } from '@intelishs-group/session-core';
import type { GrupoMigas, ListRowColor } from '@intelishs-group/ui';
import { CatalogosService, nombreCliente, nombreEmpresa } from '@services/catalogos.service';
import {
  EspacioService,
  RUTA_CLIENTES,
  RUTA_EMPRESAS,
  RUTA_NOMINAS,
  rutaDeAlcance,
  type EstadoRuta,
  type PantallaConBarra,
} from '@services/espacio.service';
import { FUNCIONES, PermisosService } from '@services/permisos.service';
import type { Alcance } from '@schemas/catalogo';

/** El padron, bajo el prefijo del modulo `personal` (2026-10-22; antes `/trabajadores`). */
export const RUTA_TRABAJADORES = '/personal/trabajadores';

/** Una entrada de navegacion ya resuelta: una pantalla de un area o una pestaña de un registro. */
export interface Entrada {
  readonly id: string;
  readonly etiqueta: string;
  /** Nombre de icono de ng-zorro. */
  readonly icono: string;
  /** Ruta absoluta. */
  readonly ruta: string;
  /** Frase corta para el tooltip. */
  readonly descripcion?: string;
}

/** Una puerta del inicio. Las que aun no se construyen van sin `ruta` y se rotulan «Próximamente». */
export interface Mosaico {
  readonly id: string;
  readonly etiqueta: string;
  readonly icono: string;
  readonly descripcion: string;
  readonly ruta?: string;
  /** Color del icono: la paleta de `ListRow`, que es del sistema. Cada area tiene el suyo. */
  readonly color: ListRowColor;
}

/** Un area del inicio: su puerta (la primera vista) y, al entrar, sus pantallas. */
export interface GrupoInicio {
  /** El codigo del modulo de identidad (`personal`, `nomina`…). */
  readonly id: string;
  readonly titulo: string;
  readonly icono: string;
  readonly color: ListRowColor;
  readonly descripcion: string;
  /**
   * La segunda linea del encabezado de la pagina del area: que hay ahi, en una frase. Sin ella, la `descripcion`
   * (la linea corta de su tarjeta en la portada, que se recorta si se alarga).
   */
  readonly subtitulo: string;
  /** La pagina del area: la `ruta` de su modulo. */
  readonly ruta: string;
  readonly mosaicos: readonly Mosaico[];
}

/** Una pantalla para el buscador «Ir a…»: la entrada y de que cuelga. */
export interface PantallaMenu {
  readonly id: string;
  readonly etiqueta: string;
  readonly icono: string;
  readonly ruta: string;
  /** El area o el camino de registros del que cuelga («Empresas › Empresa X»). */
  readonly contexto: string;
}

/**
 * Una entrada de primer nivel de un area: su tarjeta en la pagina del area (con `detalle` y `color`) y su
 * resultado en «Ir a…». Una sola definicion para los dos, para que no se desalineen.
 */
interface Puerta extends Entrada {
  readonly detalle: string;
  readonly color: ListRowColor;
}

/**
 * Un area de la aplicacion = un MODULO de la aplicacion `nomina` (id 10) en identidad. `id` es su
 * `codigo` y `ruta` su `ruta`: tienen que coincidir con lo sembrado en identidad, que es de donde sale
 * el menu del marco (nombre, icono y orden tambien, ver la tabla de `CLAUDE.md`). Aqui solo se usan
 * para la pagina de cada area, el inicio e «Ir a…».
 */
interface Area {
  readonly id: string;
  readonly titulo: string;
  readonly ruta: string;
  /** Icono, color y descripcion de su puerta en el inicio. */
  readonly icono: string;
  readonly color: ListRowColor;
  readonly descripcion: string;
  /** La segunda linea del encabezado de su pagina (si no, la `descripcion`). */
  readonly subtitulo?: string;
}

const PERSONAL: Area = {
  id: 'personal', titulo: 'Personal', ruta: '/personal', icono: 'idcard', color: 'teal',
  descripcion: 'Trabajadores y movimientos',
  subtitulo: 'Altas, movimientos y bajas de los trabajadores',
};
const NOMINA: Area = {
  id: 'nomina', titulo: 'Nómina', ruta: '/nomina', icono: 'calculator', color: 'green',
  descripcion: 'Incidencias, deducciones fijas, faltas e incapacidades y el cálculo',
};
/**
 * Reportes (2026-10-16): las salidas que se descargan: el Excel de la nómina calculada y, desde fnomina `53f36c4`
 * (ola P10), la revisión de conceptos, de empresas, de clientes y de trabajadores.
 */
const REPORTES: Area = {
  id: 'reportes', titulo: 'Reportes', ruta: '/reportes', icono: 'file-excel', color: 'purple',
  descripcion: 'Las salidas que se descargan',
};
const EMPRESAS_Y_CLIENTES: Area = {
  id: 'empresas-clientes', titulo: 'Empresas y clientes', ruta: '/empresas-clientes', icono: 'bank', color: 'blue',
  descripcion: 'Empresas, clientes y nóminas',
};
const CONFIGURACION: Area = {
  id: 'configuracion', titulo: 'Configuración', ruta: '/configuracion', icono: 'setting', color: 'orange',
  descripcion: 'Cómo se calcula: en general o por esquema, empresa, cliente y nómina',
};

/** Inicio, en «Ir a…» (en el menu lo pone identidad como el modulo `inicio`). */
const INICIO: Entrada = {
  id: 'inicio',
  etiqueta: 'Inicio',
  icono: 'home',
  ruta: '/inicio',
  descripcion: 'Las áreas del sistema',
};

/** `/configuracion/<seccion>`: las pantallas del area Configuracion (las que solo son de la general). */
function deConfiguracion(seccion: string): string {
  return `/configuracion/${seccion}`;
}

/** `/personal/trabajadores/<seccion>`: las pantallas del padron que cuelgan de la lista. */
/**
 * Las rutas que son de una pantalla sin colgar de ella (heredadas del MVP): migrar es `…/migrar` y la lista
 * `…/migraciones`. Sin esto la miga diría «Personal › Trabajadores › …». Por el `id` de la puerta. Desde fnomina
 * `5f6bfe0` las capturas y los detalles de «Altas desde archivo» y «Cambios masivos» cuelgan de su ruta.
 */
const OTRAS_RUTAS: Readonly<Record<string, readonly string[]>> = {
  migraciones: [deTrabajadores('migrar')],
};

function deTrabajadores(seccion: string): string {
  return `${RUTA_TRABAJADORES}/${seccion}`;
}

/** `/nomina/<seccion>`: las pantallas del area Nomina (2026-10-13). */
function deNomina(seccion: string): string {
  return `/nomina/${seccion}`;
}

/** `/reportes/<seccion>`: las pantallas del area Reportes (2026-10-16). */
function deReportes(seccion: string): string {
  return `/reportes/${seccion}`;
}

/**
 * Lo que la aplicacion navega por su cuenta, ahora que el menu lo da el marco de SINAY (2026-10-22).
 *
 * El MENU LATERAL ya no se arma aqui: son los modulos que identidad otorga al rol activo
 * (`<intelishs-shell-sesion>` lee `SesionService.modulos()`), uno por area. Lo que queda:
 *
 * - **Las areas y sus pantallas** (`grupos`): las areas cuyo modulo tiene la sesion
 *   (`SesionService.tieneModulo`, que exceptua al superadministrador), cada una con sus entradas. Las
 *   pinta el inicio (las puertas) y la pagina de cada area (sus tarjetas). Dentro de un area, que
 *   entradas ve cada quien es de `PermisosService` (funciones de identidad): el alta de trabajador, con
 *   `trabajadores-capturar`; bases, parametros, esquemas de pago y listas del trabajador, con
 *   `configuracion-general`; los demas, la consulta de los parametros.
 * - **Las pestañas del registro abierto** (`pestanas`) en Empresas y clientes: la empresa (sus datos,
 *   registros, estructura, turnos y sus clientes) o el cliente (datos, razones sociales, quien le
 *   factura y donde esta su personal). Antes iban anidadas en el menu; las pinta la banda del espacio.
 * - **«Ir a…»** (`pantallas`): el inicio, las areas, sus entradas y las pestañas del registro abierto
 *   (y las nominas del cliente en la empresa), con de que cuelgan.
 *
 * Ninguna entrada lleva a otra area. En SINAY (sync de `cfbed6f`): sin Administracion (usuarios y
 * perfiles los orquesta SINAY con adminportal, D19) y sin los permisos finos de `fnomina`.
 */
@Injectable({ providedIn: 'root' })
export class NavegacionService {
  private readonly sesion = inject(SesionService);
  private readonly espacio = inject(EspacioService);
  private readonly catalogos = inject(CatalogosService);
  private readonly permisos = inject(PermisosService);

  /** Las areas cuyo modulo tiene la sesion, con sus entradas de primer nivel. Sin sesion, ninguna. */
  private readonly areas = computed<readonly { readonly area: Area; readonly puertas: readonly Puerta[] }[]>(() => {
    if (!this.sesion.listo()) return [];
    // Las entradas que solo tienen sentido con su funcion: el alta guiada (capturar trabajadores) y las
    // pantallas que solo existen en la configuracion general.
    const capturar = this.permisos.puede(FUNCIONES.TRABAJADORES_CAPTURAR);
    const general = this.permisos.puede(FUNCIONES.CONFIGURACION_GENERAL);

    // Personal: quien tiene el modulo ve el padron de su alcance (nominaapi filtra y enmascara); el alta, quien
    // tiene `trabajadores-capturar` (el `TRABAJADORES_CAPTURAR` de fnomina). Desde fnomina `5f6bfe0`, por lo que
    // se hace y no por el medio (Juan, 2026-10-23; como «Bajas»): las altas y los reingresos desde un archivo en
    // «Altas desde archivo»; el mismo cambio a varios y los cambios por archivo en «Cambios masivos» (sus capturas
    // y detalles cuelgan de su ruta); los avisos al IMSS y las modificaciones del bimestre junto a «Bajas».
    // SINAY: todas para quien tiene el modulo (en fnomina, `TRABAJADORES_VER`, `BAJAS_VER` e `IMSS_VER`); hacer
    // algo es de la funcion de la accion y lo revisa la pantalla.
    const personal: Puerta[] = [
      { id: 'trabajadores', etiqueta: 'Trabajadores', icono: 'idcard', ruta: RUTA_TRABAJADORES, color: 'teal',
        descripcion: 'El personal de tu alcance a una fecha',
        detalle: 'El personal de las empresas y nóminas de tu alcance: la lista a una fecha y la ficha de cada trabajador con sus movimientos.' },
      ...(capturar
        ? [{ id: 'alta-trabajador', etiqueta: 'Alta de trabajador', icono: 'user-add', ruta: deTrabajadores('alta'),
             color: 'teal' as const, descripcion: 'Persona, contratación y asignación',
             detalle: 'El alta guiada en tres pasos: la persona, su contratación y su asignación y pago.' }]
        : []),
      { id: 'altas-archivo', etiqueta: 'Altas desde archivo', icono: 'upload', ruta: deTrabajadores('altas-archivo'),
        color: 'teal', descripcion: 'Altas nuevas y reingresos desde un archivo',
        detalle: 'Altas de trabajadores nuevos y reingresos de los que vuelven, desde un archivo de Excel o CSV con vista previa: los archivos subidos y en curso.' },
      { id: 'cambios-masivos', etiqueta: 'Cambios masivos', icono: 'usergroup-add',
        ruta: deTrabajadores('cambios-masivos'), color: 'teal',
        descripcion: 'El mismo cambio a varios o cada uno lo suyo',
        detalle: 'El mismo cambio para muchos trabajadores de una empresa o, desde un archivo, el suyo a cada uno (también la descarga de la lista corregida), con vista previa: los hechos y en curso.' },
      { id: 'migraciones', etiqueta: 'Migraciones', icono: 'swap', ruta: deTrabajadores('migraciones'),
        color: 'teal',
        descripcion: 'Personal a otra empresa pagadora',
        detalle: 'El paso del personal a otra empresa pagadora (baja en una y alta en la otra): las migraciones hechas y en curso.' },
      // Bajas (2026-10-20, etapa BJ1). SINAY: para quien tiene el modulo (en fnomina, con `BAJAS_VER`);
      // aplicarlas es de `trabajadores-capturar` y lo revisa la pantalla.
      { id: 'bajas', etiqueta: 'Bajas', icono: 'logout', ruta: deTrabajadores('bajas'), color: 'teal',
        descripcion: 'Bajas del personal y sus trámites',
        detalle: 'Las bajas del personal con lo que le falta a cada una (el aviso al IMSS y los demás trámites) y su fecha límite: dar de baja y darle seguimiento.' },
      // Avisos al IMSS (fnomina 5f6bfe0, etapa IM1): el detalle (`…/avisos-imss/:id`) cuelga de su ruta.
      { id: 'avisos-imss', etiqueta: 'Avisos al IMSS', icono: 'safety-certificate',
        ruta: deTrabajadores('avisos-imss'), color: 'teal',
        descripcion: 'Altas, modificaciones y bajas ante el IMSS',
        detalle: 'Los avisos al IMSS que se crean solos con los movimientos, con su plazo de cinco días hábiles: generar el archivo del IDSE y anotar su presentación, su rechazo o su rectificación.' },
      // Modificaciones del bimestre (fnomina 5f6bfe0, etapa IM2): el SBC que cambia sin movimiento.
      { id: 'bimestre-imss', etiqueta: 'Modificaciones del bimestre', icono: 'rise',
        ruta: deTrabajadores('bimestre-imss'), color: 'teal',
        descripcion: 'El SBC que cambia sin movimiento',
        detalle: 'A quién le cambia el SBC en el bimestre por su aniversario o por la tabla de prestaciones, para crear sus avisos de modificación; y, solo informativo, quién queda abajo del salario mínimo nuevo y el SBC topado que cambia con la UMA.' },
      // Vacaciones (fnomina 53f36c4, etapa VA2, ola P9): la carga del saldo inicial (`…/vacaciones/saldo-inicial`)
      // enciende la misma entrada (la ruta mas larga que casa). SINAY: para quien tiene el modulo (en fnomina,
      // `TRABAJADORES_VER`); el saldo inicial, los ajustes y la fecha de corte son de `trabajadores-capturar`.
      { id: 'vacaciones', etiqueta: 'Vacaciones', icono: 'schedule', ruta: deTrabajadores('vacaciones'), color: 'teal',
        descripcion: 'Días de vacaciones por trabajador',
        detalle: 'La cuenta de vacaciones de cada trabajador de la empresa: días pendientes, adelantados y devengados, los fuera de plazo y por prescribir; el saldo inicial y la fecha de corte.' },
      { id: 'no-procedieron', etiqueta: 'Altas que no procedieron', icono: 'user-delete',
        ruta: deTrabajadores('no-procedieron'), color: 'teal', descripcion: 'Altas canceladas, por empresa',
        detalle: 'Las altas que se cancelaron, por empresa, con su historial.' },
    ];

    // Nomina (2026-10-13, etapa I1): las incidencias del periodo en operacion y su consulta de periodos
    // anteriores; deducciones fijas (etapa I3), faltas e incapacidades (etapa I2) y su historico; el calculo
    // (etapa C1, 2026-10-15): «Calcular» y «Nomina calculada».
    // SINAY: quien tiene el modulo ve el area; nominaapi filtra por su alcance y calcular, capturar, cerrar y
    // reabrir los revisa cada pantalla con `puedeEscribir` (en fnomina, `INCIDENCIAS_VER` y `NOMINA_VER`).
    const nomina: Puerta[] = [
      ...this.incidencias(),
      { id: 'calcular', etiqueta: 'Calcular', icono: 'calculator', ruta: deNomina('calcular'), color: 'green' as const,
        descripcion: 'El cálculo del periodo en operación',
        detalle: 'Calcula la nómina del periodo en operación (las veces que haga falta), con su avance, lo que hay que resolver, la comparación con el periodo anterior y la prefactura de la nómina.' },
      { id: 'nomina-calculada', etiqueta: 'Nómina calculada', icono: 'table', ruta: deNomina('calculada'),
        color: 'green' as const, descripcion: 'La nómina en pantalla y el recibo de cada trabajador',
        detalle: 'El reporte de la nómina calculada de un periodo, con su costo patronal y su prefactura, y el recibo de cada trabajador con de dónde sale cada importe.' },
      // Finiquitos (fnomina 53f36c4, etapas FQ3 y FQ4, ola P9): el ensayo (`/nomina/finiquitos/ensayos/:id`) y la
      // nomina de finiquitos (`/nomina/finiquitos/:corrida`) encienden la misma entrada. SINAY: para quien tiene el
      // modulo (en fnomina, con `FINIQUITOS_VER`); calcular lo dice la API (`puede_calcular`).
      { id: 'finiquitos', etiqueta: 'Finiquitos', icono: 'solution', ruta: deNomina('finiquitos'), color: 'green' as const,
        descripcion: 'Ensayos y nóminas de finiquitos',
        detalle: 'Los finiquitos de una nómina: los ensayos de un trabajador vigente con sus escenarios, las bajas que esperan su finiquito y las nóminas de finiquitos, con lo que se le paga a cada uno, su cálculo, su recibo, el cierre y el Excel.' },
    ];

    // Reportes (2026-10-16, decision del dueño del producto: «para irnos ordenando»): las descargas salen de aqui y no
    // de la pantalla que se consulta. La primera, el Excel de la nomina calculada.
    // SINAY: para quien tiene el modulo (en fnomina, con `NOMINA_DESCARGAR`).
    const reportes: Puerta[] = [
      // 2026-10-19: con otro nombre que Nomina › «Nomina calculada» (la consulta en pantalla con el recibo).
      { id: 'reporte-nomina-calculada', etiqueta: 'Nómina y prefactura en Excel', icono: 'file-excel',
        ruta: deReportes('nomina-calculada'), color: 'purple',
        descripcion: 'Descarga la nómina calculada y su prefactura en Excel',
        detalle: 'Descarga la nómina calculada de un periodo en Excel: una hoja con la prefactura y una hoja por nómina de la cadena, con los días de faltas e incapacidades, los conceptos, el costo patronal y la prefactura de cada trabajador.' },
      // ── Reportes de revision (ola P10, fnomina 53f36c4: 2026-10-28 y 2026-10-29) ──
      // SINAY (D19): para toda sesion con el modulo (en fnomina, con CONFIGURACION_GENERAL_VER, EMPRESAS_VER,
      // CLIENTES_VER y TRABAJADORES_VER + TRABAJADORES_DESCARGAR).
      { id: 'reporte-conceptos', etiqueta: 'Conceptos', icono: 'unordered-list',
        ruta: deReportes('conceptos'), color: 'purple',
        descripcion: 'Revisa en Excel cómo quedó cada concepto',
        detalle: 'Revisa en un Excel cómo quedó cada concepto: fórmula, exenciones, a qué aplica y sus personalizaciones, en general, en un esquema o en una nómina, con lo que conviene revisar.' },
      { id: 'reporte-empresas', etiqueta: 'Empresas', icono: 'bank',
        ruta: deReportes('empresas'), color: 'purple',
        descripcion: 'Registros patronales, ubicaciones y estructura de cada empresa',
        detalle: 'Revisa en un Excel los datos de cada empresa, sus registros patronales con la prima vigente, sus ubicaciones y lugares de asignación, su estructura y su regla de lugar, con lo que conviene revisar.' },
      { id: 'reporte-clientes', etiqueta: 'Clientes', icono: 'team',
        ruta: deReportes('clientes'), color: 'purple',
        descripcion: 'Razones sociales, facturación, nóminas y periodos de cada cliente',
        detalle: 'Revisa en un Excel los datos de cada cliente, sus razones sociales, a quién se le factura, sus nóminas con su esquema y su calendario y los periodos del ejercicio, con lo que conviene revisar.' },
      { id: 'reporte-trabajadores', etiqueta: 'Trabajadores', icono: 'idcard',
        ruta: deReportes('trabajadores'), color: 'purple',
        descripcion: 'Datos, asignación, estructura, créditos y pensión de cada trabajador',
        detalle: 'Revisa en un Excel los datos de cada trabajador con lo vigente a la fecha: su asignación, su empresa y su lugar en la estructura, sus créditos y su pensión alimenticia, con lo que conviene revisar.' },
      // ── fin de los reportes de revision (ola P10) ──
    ];

    const empresasYClientes: Puerta[] = [
      { id: 'empresas', etiqueta: 'Empresas', icono: 'bank', ruta: RUTA_EMPRESAS, color: 'blue',
        descripcion: 'Datos, registros, estructura y clientes de cada empresa',
        detalle: 'Los datos de cada empresa, sus registros patronales, su estructura, sus turnos y sindicatos y sus clientes con sus nóminas.' },
      { id: 'clientes', etiqueta: 'Clientes', icono: 'team', ruta: RUTA_CLIENTES, color: 'purple',
        descripcion: 'Datos de cada cliente, quién le factura y dónde está su personal',
        detalle: 'Los datos de cada cliente, sus razones sociales, las empresas que le facturan y dónde está su personal.' },
      { id: 'nominas', etiqueta: 'Nóminas', icono: 'schedule', ruta: RUTA_NOMINAS, color: 'teal',
        descripcion: 'Todas las nóminas de tu alcance',
        detalle: 'Todas las nóminas de tu alcance con su cliente, empresa, periodicidad, esquema y facturación de hoy.' },
      // Sin «Directorio» (Juan, 2026-10-12): repetia las listas de Empresas y Clientes.
    ];

    // Los dias inhabiles del IMSS (fnomina 5f6bfe0, etapa IM1): solo en la general, junto a los parametros; quien no
    // tiene `configuracion-general` los consulta. SINAY: para todos (en fnomina, la consulta con `IMSS_VER` o `BAJAS_VER`).
    const diasInhabiles: Puerta = {
      id: 'dias-inhabiles-imss', etiqueta: 'Días inhábiles del IMSS', icono: 'calendar',
      ruta: deConfiguracion('dias-inhabiles-imss'), color: 'orange',
      descripcion: 'Días que no cuentan en el plazo de los avisos y las bajas',
      detalle: 'Los días que el IMSS declara inhábiles, por año: no cuentan en el plazo de cinco días hábiles de los avisos al IMSS ni de las bajas.' };

    // Las pantallas con barra: para todos (sin su funcion, en solo lectura; la general, con `configuracion-general`).
    const configuracion: Puerta[] = [
      this.conBarra('conceptos', 'Conceptos', 'unordered-list', 'Fórmulas, ISR, IMSS e ISN de cada concepto',
        'Las fórmulas y el comportamiento en ISR, IMSS e ISN de cada concepto; eliges arriba a qué aplica.'),
      this.conBarra('variables', 'Variables fijas', 'sliders', 'Valores fijos del cálculo',
        'Los valores fijos del cálculo; eliges arriba a qué aplica.'),
      this.conBarra('prestaciones', 'Prestaciones', 'gift', 'Qué tabla de prestaciones aplica y desde cuándo',
        'Las tablas de prestaciones (aguinaldo, vacaciones y prima) y cuál aplica desde cuándo; eliges arriba a qué aplica.'),
      this.conBarra('regla-lugar', 'Regla del ISN y la zona', 'environment',
        'Si el ISN y la zona van por la ubicación o por el lugar de asignación',
        'Si el ISN y la zona del salario mínimo van por la ubicación o por el lugar de asignación; eliges arriba a qué aplica.'),
      this.conBarra('campos-trabajador', 'Campos del trabajador', 'form',
        'Qué datos del trabajador son obligatorios para cada proceso y cuáles se ocultan',
        'Qué datos del trabajador son obligatorios para cada proceso y cuáles se ocultan; eliges arriba a qué aplica.'),
      this.conBarra('calendario', 'Calendario de periodos', 'calendar', 'Fechas, pago, días y mes de cada periodo',
        'Las fechas, el pago, los días y el mes de cada periodo de una nómina; eliges arriba la nómina.'),
      ...(general
        ? [
            { id: 'bases', etiqueta: 'Bases nombradas', icono: 'function', ruta: deConfiguracion('bases'),
              color: 'orange' as const, descripcion: 'Fórmulas reutilizables por los conceptos (solo en la general)',
              detalle: 'Las fórmulas que reutilizan los conceptos.' },
            { id: 'parametros', etiqueta: 'Parámetros del ejercicio', icono: 'table', ruta: deConfiguracion('parametros'),
              color: 'orange' as const, descripcion: 'UMA, salario mínimo, tarifas, cuotas e ISN por vigencia',
              detalle: 'UMA, salario mínimo, tarifas del ISR, subsidio, cuotas del IMSS, ISN por estado e IVA por vigencia, con su fundamento.' },
            diasInhabiles,
            { id: 'esquemas', etiqueta: 'Esquemas de pago', icono: 'partition', ruta: deConfiguracion('esquemas'),
              color: 'orange' as const, descripcion: 'Alta y edición de los esquemas de pago',
              detalle: 'Alta y edición de los esquemas de pago; lo que aplica a cada uno se configura eligiéndolo arriba de cada pantalla.' },
            { id: 'listas-trabajador', etiqueta: 'Listas del trabajador', icono: 'ordered-list',
              ruta: deConfiguracion('listas-trabajador'), color: 'orange' as const,
              descripcion: 'Las opciones de las listas que se eligen en la ficha de cada trabajador',
              detalle: 'Las opciones de las listas que se eligen en la ficha de cada trabajador.' },
          ]
        : [
            // Quien no configura la general solo consulta los parametros: un lugar y un nombre.
            { id: 'parametros', etiqueta: 'Parámetros del ejercicio', icono: 'table',
              ruta: deConfiguracion('consulta-parametros'),
              color: 'orange' as const, descripcion: 'UMA, salario mínimo, tarifas, cuotas e ISN vigentes (consulta)',
              detalle: 'UMA, salario mínimo, tarifas del ISR, subsidio, cuotas del IMSS, ISN por estado e IVA: lo que rige en una fecha, con su fundamento y su historial.' },
            // Los dias inhabiles del IMSS, en consulta.
            diasInhabiles,
          ]),
    ];

    return [
      { area: PERSONAL, puertas: personal },
      { area: NOMINA, puertas: nomina },
      { area: REPORTES, puertas: reportes },
      { area: EMPRESAS_Y_CLIENTES, puertas: empresasYClientes },
      { area: CONFIGURACION, puertas: configuracion },
    ].filter((a) => a.puertas.length && this.sesion.tieneModulo(a.area.id));
  });

  /** Las areas de la sesion con sus pantallas: las puertas del inicio y las tarjetas de la pagina de cada area. */
  readonly grupos = computed<readonly GrupoInicio[]>(() =>
    this.areas().map(({ area, puertas }) => ({
      id: area.id,
      titulo: area.titulo,
      icono: area.icono,
      color: area.color,
      descripcion: area.descripcion,
      subtitulo: area.subtitulo ?? area.descripcion,
      ruta: area.ruta,
      mosaicos: puertas.map((d) => ({
        id: d.id,
        etiqueta: d.etiqueta,
        icono: d.icono,
        ruta: d.ruta,
        color: d.color,
        descripcion: d.detalle,
      })),
    })),
  );

  /**
   * El catálogo de las migas (`proveerMigas` en `app.config.ts`): cada área con sus pantallas, de las MISMAS
   * puertas que pintan sus tarjetas, así que una pantalla nueva tiene miga sin tocar nada aquí. `otrasRutas`
   * cubre las rutas heredadas del MVP que no cuelgan de su pantalla (ver `OTRAS_RUTAS`).
   */
  readonly gruposMigas = computed<readonly GrupoMigas[]>(() =>
    this.grupos().map((g) => ({
      etiqueta: g.titulo,
      icono: g.icono,
      ruta: g.ruta,
      pantallas: g.mosaicos
        .filter((m): m is Mosaico & { ruta: string } => !!m.ruta)
        .map((m) => ({ etiqueta: m.etiqueta, ruta: m.ruta, otrasRutas: OTRAS_RUTAS[m.id] })),
    })),
  );

  /**
   * Las pestañas del registro abierto en Empresas y clientes: las secciones de la empresa o del cliente
   * que el usuario puede ver. Fuera de un registro, o si su id no existe, ninguna (la pantalla lo dice).
   * El cliente en una empresa no tiene fila propia: cuelga de «Clientes en esta empresa».
   */
  readonly pestanas = computed<readonly Entrada[]>(() => {
    const r = this.espacio.ruta();
    if (this.espacio.estado() === 'no-existe') return [];
    if (r.espacio === 'EMPRESAS' && r.empresa) return this.seccionesEmpresa(r);
    if (r.espacio === 'CLIENTES' && r.cliente) return this.seccionesCliente(r);
    return [];
  });

  /**
   * Las pantallas para «Ir a…», con de que cuelgan: el inicio, cada area y sus entradas y, del registro
   * abierto, sus pestañas (y las nominas del cliente en la empresa).
   */
  readonly pantallas = computed<readonly PantallaMenu[]>(() => {
    const salida: PantallaMenu[] = [{ ...INICIO, contexto: '' }];
    for (const { area, puertas } of this.areas()) {
      salida.push({ id: area.id, etiqueta: area.titulo, icono: area.icono, ruta: area.ruta, contexto: 'Área' });
      salida.push(...puertas.map((p) => ({ id: p.id, etiqueta: p.etiqueta, icono: p.icono, ruta: p.ruta, contexto: area.titulo })));
    }
    const r = this.espacio.ruta();
    const c = this.catalogos.datos();
    const pestanas = this.pestanas();
    if (pestanas.length && r.espacio === 'EMPRESAS') {
      const empresa = c ? nombreEmpresa(c, Number(r.empresa)) : `Empresa ${r.empresa}`;
      salida.push(...pestanas.map((p) => ({ ...p, contexto: `Empresas › ${empresa}` })));
      if (r.cliente) {
        const cliente = c ? nombreCliente(c, Number(r.cliente)) : `Cliente ${r.cliente}`;
        const contexto = `Empresas › ${empresa} › Clientes en esta empresa › ${cliente}`;
        salida.push(...this.seccionesClienteEnEmpresa(r).map((p) => ({ ...p, contexto })));
      }
    } else if (pestanas.length && r.espacio === 'CLIENTES') {
      const cliente = c ? nombreCliente(c, Number(r.cliente)) : `Cliente ${r.cliente}`;
      salida.push(...pestanas.map((p) => ({ ...p, contexto: `Clientes › ${cliente}` })));
    }
    return salida;
  });

  /** Si la sesion tiene el modulo de un area (`personal`, `empresas-clientes`…): lo usa «Ir a…». */
  tieneArea(codigo: string): boolean {
    return this.sesion.tieneModulo(codigo);
  }

  /** Las pantallas de las incidencias del area Nomina (SINAY: para quien tiene el modulo; en fnomina, con `INCIDENCIAS_VER`). */
  private incidencias(): Puerta[] {
    return [
      { id: 'incidencias', etiqueta: 'Incidencias', icono: 'file-text', ruta: deNomina('incidencias'), color: 'green',
        descripcion: 'Lo que se captura en el periodo que se está operando',
        detalle: 'Lo que se paga o descuenta en el periodo que se está operando de una nómina: captura rápida, la hoja con todo lo capturado y la carga desde un archivo.' },
      // Etapa I3 (2026-10-14): lo que se registra una vez y se aplica solo en cada periodo (prestamos y
      // deducciones por fechas) y la consulta de creditos y pension de la nomina.
      { id: 'deducciones-fijas', etiqueta: 'Deducciones fijas', icono: 'credit-card',
        ruta: deNomina('deducciones-fijas'), color: 'green',
        descripcion: 'Préstamos y deducciones que se aplican solas cada periodo',
        detalle: 'Los préstamos y las deducciones que se registran una vez a un trabajador y se aplican solas en cada periodo, con su saldo y su plan, y la consulta de sus créditos INFONAVIT y FONACOT y su pensión alimenticia.' },
      // Etapa I2 (2026-10-13; corregida el mismo dia): faltas por cantidad e incapacidades con saldo, en el
      // periodo en operacion.
      { id: 'faltas-incapacidades', etiqueta: 'Faltas e incapacidades', icono: 'calendar',
        ruta: deNomina('faltas-incapacidades'), color: 'green',
        descripcion: 'Días que no se trabajaron, en el periodo en operación',
        detalle: 'Las faltas, permisos e incapacidades que se descuentan en el periodo que se está operando, lo que queda de cada incapacidad o suspensión y el calendario de cada trabajador.' },
      // El historico (2026-10-14): por mes o por fechas, de una nomina o de un trabajador.
      { id: 'historico-ausencias', etiqueta: 'Histórico de faltas e incapacidades', icono: 'file-search',
        ruta: deNomina('faltas-incapacidades/historico'), color: 'green',
        descripcion: 'Consulta por mes o por fechas',
        detalle: 'Las faltas, permisos, incapacidades y suspensiones de una nómina o de un trabajador en un mes o entre dos fechas, por el periodo en que se descontaron o por la fecha en que sucedieron, para consultar y descargar.' },
      { id: 'incidencias-anteriores', etiqueta: 'Incidencias anteriores', icono: 'history',
        ruta: deNomina('incidencias/anteriores'), color: 'green',
        descripcion: 'Consulta de periodos anteriores o de todo el ejercicio',
        detalle: 'Lo que se capturó en otros periodos de una nómina o en todo el ejercicio, solo para consultar y descargar.' },
    ];
  }

  /**
   * Una pantalla de Configuracion con la barra: su ruta lleva el nivel en el que se esta (o el ultimo
   * elegido) si la pantalla lo admite, para conservarlo al pasar de una a otra.
   */
  private conBarra(
    pantalla: PantallaConBarra,
    etiqueta: string,
    icono: string,
    descripcion: string,
    detalle: string,
  ): Puerta {
    return {
      id: pantalla,
      etiqueta,
      icono,
      ruta: this.espacio.rutaConfiguracion(pantalla),
      color: 'orange',
      descripcion,
      detalle,
    };
  }

  /** Las secciones de la empresa abierta: sus datos, registros, estructura, turnos y sus clientes. */
  private seccionesEmpresa(r: EstadoRuta): Entrada[] {
    const empresa: Alcance = { empresa_id: r.empresa };
    const base = rutaDeAlcance(empresa);
    return this.visibles(empresa, base, [
      { id: 'emp-datos', etiqueta: 'Datos de la empresa', icono: 'profile', ruta: `${base}/datos`,
        descripcion: 'Razón social, RFC, domicilio fiscal y esquemas que paga' },
      { id: 'emp-registros', etiqueta: 'Registros patronales', icono: 'safety-certificate', ruta: `${base}/registros`,
        descripcion: 'Sus registros y su prima de riesgo de trabajo' },
      { id: 'emp-estructura', etiqueta: 'Estructura', icono: 'apartment', ruta: `${base}/estructura`,
        descripcion: 'Ubicaciones, lugares de asignación y catálogos por nivel' },
      { id: 'emp-turnos', etiqueta: 'Turnos y sindicatos', icono: 'clock-circle', ruta: `${base}/turnos`,
        descripcion: 'Los turnos y sindicatos que se eligen en la ficha de sus trabajadores' },
      { id: 'emp-clientes', etiqueta: 'Clientes en esta empresa', icono: 'team', ruta: `${base}/clientes`,
        descripcion: 'A quién le factura y qué clientes tienen su personal aquí' },
    ]);
  }

  /** Las secciones del cliente en la empresa: sus nominas (para «Ir a…»; en la pagina cuelgan de sus clientes). */
  private seccionesClienteEnEmpresa(r: EstadoRuta): Entrada[] {
    const enEmpresa: Alcance = { empresa_id: r.empresa, cliente_id: r.cliente };
    const base = rutaDeAlcance(enEmpresa);
    return this.visibles(enEmpresa, base, [
      { id: 'ce-nominas', etiqueta: 'Nóminas', icono: 'schedule', ruta: `${base}/nominas`,
        descripcion: 'Las nóminas del cliente en esta empresa: esquema, facturación y cadena' },
    ]);
  }

  /** Las secciones del cliente abierto. */
  private seccionesCliente(r: EstadoRuta): Entrada[] {
    const cliente: Alcance = { cliente_id: r.cliente };
    const base = rutaDeAlcance(cliente);
    return this.visibles(cliente, base, [
      { id: 'cli-datos', etiqueta: 'Datos del cliente', icono: 'profile', ruta: `${base}/datos`,
        descripcion: 'Nombre, grupo y estado del cliente' },
      { id: 'cli-razones', etiqueta: 'Razones sociales', icono: 'solution', ruta: `${base}/razones-sociales`,
        descripcion: 'A quién se le factura: cada razón social con su RFC' },
      { id: 'cli-facturadoras', etiqueta: 'Empresas que le facturan', icono: 'audit', ruta: `${base}/facturadoras`,
        descripcion: 'Quién le cobra el servicio a cada razón social y desde cuándo' },
      { id: 'cli-empresas', etiqueta: 'Empresas donde está su personal', icono: 'bank', ruta: `${base}/empresas`,
        descripcion: 'Las empresas pagadoras donde está y sus nóminas en cada una' },
    ]);
  }

  /**
   * Las secciones de un registro que el usuario puede ver (`EspacioService.puedeVerSeccion`, el mismo
   * que usa «Cambiar»). La seccion es el primer segmento de su ruta despues de `base`.
   */
  private visibles(a: Alcance, base: string, entradas: readonly Entrada[]): Entrada[] {
    return entradas.filter((m) => this.espacio.puedeVerSeccion(a, m.ruta.slice(base.length + 1).split('/')[0]));
  }
}
